import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import DeleteModal from "../ItemGroup/DeleteModal";
import ListAdminButtons from "../Common/ListAdminButtons";
import SpinnerLoading from "../Common/SpinnerLoading";
import { itemGroupServices } from "../../Services";
import { AuthContext } from "../../Contexts/AuthContext";
import { GlobalContext } from "../../Contexts/GlobalContextProvider";
import Tooltip from "../../Components/Common/Tooltip";
import { roles } from "../../Config/Roles";

const ItemGroupTab = ({ tenantId }, props) => {
  const history = useHistory();
  const { t: translator } = useTranslation();
  const { roleId } = useContext(AuthContext);
  const { locTab } = useContext(GlobalContext);
  const { cashier, accountant, manager } = roles;

  const [pagination, setPagination] = useState({
    totalCount: 0,
    pageSize: 5,
    pageIndex: 0,
  });
  const [itemGroups, setItemGroups] = useState([]);
  const [update, setUpdate] = useState(new Date());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState([]);
  const [filters, setFilters] = useState({
    searchName: "",
  });

  useEffect(() => {
    if (locTab === "itemGroup") {
      console.log(locTab);
      setIsLoading(true);
      itemGroupServices
        .getSearch({
          tenantId: Number(tenantId),
          searchName: filters.searchName,
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
        })
        .then(data => {
          setIsLoading(false);
          setItemGroups(data.data.itemGroups);
          setPagination({ ...pagination, totalCount: data.data.totalCount });
        });
    }
  }, [tenantId, filters, update, locTab]);

  const handleToAdd = () => {
    history.push(`/add-item-group/${tenantId}`);
  };

  const handleToUpdate = () => {
    if (selectedItems.length !== 1) return false;
    history.push(`/edit-item-group/${tenantId}/${selectedItems}`);
  };

  const handleInputChange = event => {
    const { name, value } = event.target;
    setFilters({ ...filters, [name]: value });
  };

  const handleHideDeleteModal = () => setShowDeleteModal(false);

  const confirmDelete = () => {
    let deletePromises = [];

    selectedItems.forEach(id => {
      deletePromises.push(itemGroupServices.deletePriceList(id));
    });

    Promise.all(deletePromises).then(data => {
      setUpdate(new Date());
      setShowDeleteModal(false);
    });
  };

  const handleOnSelect = (row, isSelect) => {
    if (isSelect) {
      setSelectedItems([...selectedItems, row.itemGroupId]);
    } else {
      setSelectedItems(selectedItems.filter(p => p !== row.itemGroupId));
    }
  };

  const handleOnSelectAll = isSelect => {
    if (isSelect) {
      setSelectedItems(itemGroups.map(x => x.itemGroupId));
    } else {
      setSelectedItems([]);
    }
  };

  const handleTableChange = (type, { page, sizePerPage }) => {
    setPagination({
      ...pagination,
      pageIndex: page - 1,
      pageSize: sizePerPage,
    });
    setUpdate(new Date());
  };

  const RemoteAll = ({ data, page, sizePerPage, totalSize, onTableChange }) => (
    <div>
      <BootstrapTable
        remote
        keyField="itemGroupId"
        data={data}
        columns={columns}
        rowClasses="boostrap-table-row"
        noDataIndication={translator("No-Data")}
        pagination={paginationFactory({
          page,
          sizePerPage,
          totalSize,
        })}
        onTableChange={onTableChange}
      />
    </div>
  );

  function actionFormatter(cell, row) {
    return (
      <div className="action">
        <label className="labelCont">
          <input
            type="checkbox"
            name="workingMode"
            checked={selectedItems.find(x => x === row.itemGroupId)}
            onChange={e => handleOnSelect(row, e.target.checked)}
          />
          <span className="checkmark"></span>
        </label>
      </div>
    );
  }

  function actionHeaderFormatter(cell, rows) {
    return (
      <div className="action">
        <label className="labelCont">
          <input
            type="checkbox"
            name="workingMode"
            checked={
              itemGroups &&
              itemGroups.length > 0 &&
              !itemGroups
                .map(p => p.itemGroupId)
                .some(p => selectedItems.indexOf(p) < 0)
            }
            onChange={e => handleOnSelectAll(e.target.checked)}
          />
          <span className="checkmark"></span>
        </label>
      </div>
    );
  }

  const columns = [
    {
      dataField: "itemGroupId",
      text: "",
      headerStyle: (colum, colIndex) => {
        return { width: "50px", textAlign: "center" };
      },
      headerFormatter: actionHeaderFormatter,
      formatter: actionFormatter,
      hidden: [cashier, accountant, manager].includes(Number(roleId))
        ? true
        : false,
    },
    {
      dataField: "name",
      text: translator("TenantDetail-ItemGroupTab-TableHeader-Name"),
      sort: true,
    },
  ];

  return (
    <div className="p-2">
      <div className="container-fluid p-0">
        <div className="card-body pl-0">
          <div className="form-inline">
            <input
              style={{ width: "180px", marginRight: "6px" }}
              name="searchName"
              type="text"
              className="form-control"
              placeholder={translator("ItemGroup-Tab-Search")}
              defaultValue={filters.searchName}
              onChange={handleInputChange}
            />
            <Tooltip text={translator("Tabs-Search")} />
          </div>
          <ListAdminButtons
            selectedItemsLength={selectedItems.length}
            handleToAdd={
              [cashier, accountant, manager].includes(Number(roleId))
                ? null
                : handleToAdd
            }
            handleToUpdate={
              [cashier, accountant, manager].includes(Number(roleId))
                ? null
                : handleToUpdate
            }
            handleDelete={
              [cashier, accountant, manager].includes(Number(roleId))
                ? null
                : () => setShowDeleteModal(true)
            }
          />

          {isLoading && (
            <div className="dashboard-card-spinner">
              <SpinnerLoading />
            </div>
          )}

          {!isLoading && (
            <RemoteAll
              data={itemGroups}
              page={pagination.pageIndex + 1}
              sizePerPage={pagination.pageSize}
              totalSize={pagination.totalCount}
              onTableChange={handleTableChange}
            />
          )}
        </div>
      </div>

      <DeleteModal
        show={showDeleteModal}
        handleHide={handleHideDeleteModal}
        title={translator("TenantDetail-ItemGroupTab-DeleteModal-Title")}
        text={translator("TenantDetail-ItemGroupTab-DeleteModal-Text")}
        confirmDelete={confirmDelete}
      />
    </div>
  );
};

export default ItemGroupTab;
